import * as _ from 'lodash-es';
import { useState } from 'react';

import { kebabCase } from '@/utils';

import {
  exportDrawingReport,
  exportProjectReport,
  exportProjectForVendor,
  exportDrawingForVendor,
  exportMarkedUpDrawing,
  exportSEVDrawing,
  exportSEVProject,
  exportInsulationDrawing,
  exportInsulationProject,
  exportPoundageSummary,
  exportPoundageProject,
  exportPipesForPage,
  exportPipesForProject,
  saveAsFile,
} from '../api';
import { ExportFuncType } from '../components';
import {
  SUMMARY_XP_FORMAT as FMT,
  EXPORT_REPORT_SCALE as ERS,
  SymbolEditParams,
  ExportScope,
} from '../types';

/**
 * takeoff tools state and handlers
 * @2025/09/16
 */
export const useTakeoffTools = (context: SymbolEditParams) => {
  //
  const [downloading, setDownloading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [togglePreview, setTogglePreview] = useState(true);
  const [printing, setPrinting] = useState(false);

  const { projectName } = context;

  const suffix: { [P in FMT]: string } = {
    EXCEL: 'xlsx',
    CSV: 'csv',
    PDF: 'pdf',
    PROJECT: 'xlsx', // @2024/11/06
    xls: 'xlsx', // @2024/01/23
    doc: 'docx', // @2026/04/14
  };

  const generateReportName = (
    layout: string | undefined,
    isProjectScale: boolean,
    format = 'xls',
  ) => {
    const { fileName, pageName } = context;
    const optionalLayout = layout ? `_${layout}` : '';
    const singlePageFile = `${fileName}_${pageName}${optionalLayout}`;
    const finalReportName = isProjectScale
      ? _.snakeCase(projectName) + optionalLayout
      : singlePageFile;
    return `${finalReportName}.${suffix[format as FMT]}`;
  };

  const downloadReportAs: ExportFuncType = async (
    format: string,
    scale: string,
    layout?: string,
    group = true,
    segment_size = 1,
  ) => {
    setDownloading(true);
    const isProjectScale = scale === ERS.P;
    // console.log(`downloading ${format} file ...`);
    const response = isProjectScale
      ? await exportProjectReport(context, format, layout)
      : await exportDrawingReport(context, format, layout, group, segment_size);
    const fullFileName = generateReportName(layout, isProjectScale, format);
    saveAsFile(response, fullFileName);
    setTimeout(() => setDownloading(false));
  };
  /**
   * for SEV export
   * @param scale
   */
  const generateSEVReport = async (scale: string) => {
    setDownloading(true);
    const isProjectScale = scale === ERS.P;
    const response = isProjectScale
      ? await exportSEVProject(context)
      : await exportSEVDrawing(context);
    const fullFileName = generateReportName('sev', isProjectScale);
    saveAsFile(response, fullFileName);
    setTimeout(() => setDownloading(false));
  };

  const generateInsulationReport = async (scale: string) => {
    setDownloading(true);
    const isProjectScale = scale === ERS.P;
    const response = isProjectScale
      ? await exportInsulationProject(context)
      : await exportInsulationDrawing(context);
    const fullFileName = generateReportName('insulation', isProjectScale);
    saveAsFile(response, fullFileName);
    setTimeout(() => setDownloading(false));
  };

  const generatePoundageReport = async () => {
    setDownloading(true);
    const response = await exportPoundageSummary(context);
    const fullFileName = generateReportName('poundage', false);
    saveAsFile(response, fullFileName);
    setTimeout(() => setDownloading(false));
  };

  const generatePoundageProjectReport = async () => {
    setDownloading(true);
    const response = await exportPoundageProject(context);
    const fullFileName = generateReportName('poundage', true);
    saveAsFile(response, fullFileName);
    setTimeout(() => setDownloading(false));
  };

  const generatePipesReport = async () => {
    setDownloading(true);
    const response = await exportPipesForPage(context);
    const fullFileName = generateReportName('pipes', false);
    saveAsFile(response, fullFileName);
    setTimeout(() => setDownloading(false));
  };

  const generatePipesProjectReport = async () => {
    setDownloading(true);
    const response = await exportPipesForProject(context);
    const fullFileName = generateReportName('pipes', true);
    saveAsFile(response, fullFileName);
    setTimeout(() => setDownloading(false));
  };

  const previewReportToggleHandler = () => {
    // FIXME: stop toggling this view, it produce `memory leak`!!!
    // @2024/12/18
    // setTogglePreview(!togglePreview);
  };

  /**
   * export vendor needed report data
   * @param vendorId fast_duct or wendes
   * @param format
   * @param params optional params
   */
  const generateReportForVendor = async (
    vendorId: string,
    format: string,
    scope: string,
    params = '',
  ) => {
    setCreating(true);
    if (scope == ExportScope.PG) {
      const response = await exportDrawingForVendor(context, vendorId, params);
      const singlePageFile = `${vendorId}_${context.fileName}_${context.pageName}`;
      const fullFileName = `${singlePageFile}.${format}`;
      saveAsFile(response, fullFileName);
    }
    if (scope == ExportScope.PJ) {
      const response = await exportProjectForVendor(context, vendorId, params);
      const singleProjectFile = `${vendorId}_${kebabCase(context.projectName)}`;
      const fullFileName = `${singleProjectFile}.${format}`;
      saveAsFile(response, fullFileName);
    }
    setTimeout(() => setCreating(false));
  };

  const printMarkedupPDF = async () => {
    setPrinting(true);
    const payload = {
      projectId: context.projectId,
      fileId: context.fileId,
      pageId: context.pageId,
      jobId: context.jobId,
    };
    const response = await exportMarkedUpDrawing(payload);
    const { fileName, pageName } = context;
    saveAsFile(response, `marked-${fileName}-${pageName}.pdf`);
    setPrinting(false);
  };

  return {
    togglePreview,
    creating,
    downloading,
    printing,
    downloadReportAs,
    generateSEVReport,
    generateInsulationReport,
    generatePoundageReport,
    generatePoundageProjectReport,
    generatePipesReport,
    generatePipesProjectReport,
    generateReportForVendor,
    printMarkedupPDF,
  };
};
